const SEAL_IMAGE_SELECTOR = [
  'section[class*="seal-"] img',
  '[class^="seal-"] img',
  '[class*=" seal-"] img',
  'img[class^="seal-"]',
  'img[class*=" seal-"]',
  '.product-media-container img',
  'header-menu img',
  '.menu-drawer img',
].join(',');

const SEAL_IMAGE_SURFACE_SELECTOR = [
  '[data-seal-image-loading-surface]',
  '.product-media-container',
  '[class*="__media"]',
  '[class*="__visual"]',
  '[class*="__image-wrapper"]',
  '[class*="__image-wrap"]',
  '[class*="__picture"]',
  '[class*="__poster"]',
  '[class*="__photo"]',
  '[class*="__figure"]',
  '[class*="__thumb"]',
].join(',');

const SKIP_SELECTOR = [
  '[data-seal-image-loading-skip]',
  '.resource-list__carousel .card-gallery',
  '.seal-home-hero__avatars',
  '.shopify-payment-button',
  '.quick-add-modal__skeleton',
  '.seal-recently-viewed__skeleton-card',
].join(',');

const observedImages = new WeakSet();
const observedSurfaces = new WeakSet();
const navigationSurfaces = new WeakMap();
const NAVIGATION_SURFACE_SELECTOR = [
  '.seal-mobile-menu__thumbnail',
  '.seal-mobile-starter__image',
  '.seal-mobile-system__product-image',
  '.seal-mobile-mason-products__image',
  '.seal-mobile-accessories__image',
  '.seal-system-menu__image',
  '.resource-card__media',
].join(',');

const visibleObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting && entry.target instanceof HTMLElement) {
        entry.target.dataset.sealImageVisible = 'true';
        visibleObserver.unobserve(entry.target);
      }
    }
  },
  { rootMargin: '600px 0px' }
);

function isSmallDecorativeImage(image) {
  const width = Number(image.getAttribute('width')) || image.naturalWidth || 0;
  const height = Number(image.getAttribute('height')) || image.naturalHeight || 0;

  return width > 0 && height > 0 && width <= 96 && height <= 96;
}

function getImageSurface(image) {
  const picture = image.closest('picture');
  let surface = image.closest(SEAL_IMAGE_SURFACE_SELECTOR);

  if (!surface || surface === image) {
    surface = picture?.parentElement || image.parentElement;
  }

  if (surface instanceof HTMLPictureElement) {
    surface = surface.parentElement;
  }

  if (!(surface instanceof HTMLElement) || surface === document.body || surface === document.documentElement) {
    return null;
  }

  return surface;
}

function markSurfaceLoaded(surface) {
  surface.dataset.sealImageLoaded = 'true';
  surface.classList.add('seal-image-loading');
}

function syncNavigationImage(image) {
  if (!image.closest('header-menu, .menu-drawer')) return false;
  if (image.closest('.seal-mobile-menu-brand, .drawer-localization') || isSmallDecorativeImage(image)) return true;
  if (image.classList.contains('resource-card__image--secondary')) return true;

  const surface = image.closest(NAVIGATION_SURFACE_SELECTOR);
  const target = surface || image;
  let state = navigationSurfaces.get(target);
  if (!state) {
    state = { images: new WeakSet(), pending: false, revision: 0 };
    navigationSurfaces.set(target, state);
  }

  const images = surface
    ? [...surface.querySelectorAll('img:not(.resource-card__image--secondary)')]
    : [image];

  for (const member of images) {
    member.classList.add('seal-navigation-image');
    if (state.images.has(member)) continue;
    state.images.add(member);
    member.addEventListener('load', () => syncNavigationImage(member));
    member.addEventListener('error', () => syncNavigationImage(member));
  }

  const revision = ++state.revision;
  if (images.some((member) => !member.complete)) {
    state.pending = true;
    images.forEach((member) => { member.dataset.sealNavigationImage = 'loading'; });
    if (surface) {
      surface.classList.add('seal-image-loading', 'seal-image-loading--navigation');
      surface.dataset.sealImageLoaded = 'false';
      if (!observedSurfaces.has(surface)) {
        observedSurfaces.add(surface);
        visibleObserver.observe(surface);
      }
    }
    return true;
  }

  const reveal = () => {
    if (revision !== state.revision || images.some((member) => !member.complete)) return;
    images.forEach((member) => {
      member.dataset.sealNavigationImage = member.naturalWidth > 0 ? 'ready' : 'error';
    });
    if (surface) surface.dataset.sealImageLoaded = 'true';
    state.pending = false;
  };

  if (state.pending) {
    Promise.allSettled(images.map((member) => member.naturalWidth > 0 ? member.decode() : Promise.resolve()))
      .then(reveal);
  } else {
    reveal();
  }
  return true;
}

function syncImageLoading(image) {
  if (!(image instanceof HTMLImageElement)) return;
  if (syncNavigationImage(image)) return;
  if (observedImages.has(image)) return;
  if (image.closest(SKIP_SELECTOR) || isSmallDecorativeImage(image)) return;

  const surface = getImageSurface(image);
  if (!surface) return;

  observedImages.add(image);

  if (image.complete) {
    markSurfaceLoaded(surface);
    return;
  }

  surface.dataset.sealImageLoaded = 'false';
  surface.classList.add('seal-image-loading');

  if (!observedSurfaces.has(surface)) {
    observedSurfaces.add(surface);
    visibleObserver.observe(surface);
  }

  image.addEventListener('load', () => markSurfaceLoaded(surface), { once: true });
  image.addEventListener('error', () => markSurfaceLoaded(surface), { once: true });
}

function syncSealImages(root = document) {
  if (root instanceof HTMLImageElement && root.matches(SEAL_IMAGE_SELECTOR)) {
    syncImageLoading(root);
  }

  root.querySelectorAll?.(SEAL_IMAGE_SELECTOR).forEach(syncImageLoading);
}

syncSealImages();

new MutationObserver((mutations) => {
  const navigationRoots = new Set();
  for (const mutation of mutations) {
    if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
      const navigation = mutation.target.closest('header-menu, .menu-drawer')
        || (mutation.target.matches('.menu-drawer-container') && mutation.target.querySelector('.menu-drawer'));
      if (navigation) navigationRoots.add(navigation);
      continue;
    }
    for (const node of mutation.addedNodes) {
      if (node instanceof HTMLElement) {
        syncSealImages(node);
      }
    }
  }
  navigationRoots.forEach(syncSealImages);
}).observe(document.documentElement, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['hidden', 'open', 'aria-expanded'],
});
